/**
 * Pure decision function: should a given requester be allowed to
 * export a given channel? Run BEFORE any data collection / format
 * work so the UI can refuse the export with a typed reason rather
 * than collecting messages it will then discard.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ChatExportPolicy.swift` in
 * spirit but flips the surface to "can-or-cannot" — the iOS variant
 * surfaces a warning severity for the UI banner, whereas the web
 * variant decides if the export proceeds at all. Both layers will
 * exist eventually; this file lands the gate-keeper first.
 *
 * THREAT MODEL
 *   * `requester-not-member`: a requester who is not a member of the
 *     channel must not be able to export it. Even if the local store
 *     somehow has the messages (e.g. they were a member at one point
 *     and a stale collection slipped through), the policy refuses the
 *     export so a forensic dump doesn't end up containing channels
 *     the user has since been removed from.
 *   * `channel-export-disabled`: some channels carry an admin-set
 *     "no-export" flag (e.g. legal-sensitive workspaces). The policy
 *     honours that flag — exporting silently anyway would defeat the
 *     workspace control.
 *   * `too-many-messages`: hard cap mirror at the policy layer so the
 *     UI surfaces "pick a smaller window" without invoking the
 *     formatter's cap check after a long collection pass.
 *
 * The function is PURE — no I/O, no clock — so the self-test can
 * exhaustively cover every (member × disabled × messageCount)
 * combination in milliseconds.
 */

import { MAX_MESSAGES_PER_EXPORT } from "./ChatExportFormatter";

/**
 * Minimum channel-shaped info the policy needs. The caller's
 * full channel model surely has more fields — this is the slice the
 * policy ACTUALLY reads, so a test fixture only needs to fill these.
 */
export interface ChatExportChannel {
  /** Stable channel identifier. Used in error context, not for decisions. */
  readonly channelId: string;
  /**
   * userIds of the channel's current members. The roster of users
   * whose `requesterUserId` is allowed to ask for an export.
   */
  readonly memberUserIds: readonly string[];
  /**
   * Admin-set "no exports of this channel" flag. When true, ALL
   * exports are refused regardless of requester membership.
   */
  readonly isExportDisabled: boolean;
  /**
   * Number of messages that would be exported under the current
   * collection window. The policy gates against
   * `MAX_MESSAGES_PER_EXPORT` here so the UI doesn't have to.
   */
  readonly messageCount: number;
}

/**
 * Minimum requester-shaped info the policy needs.
 */
export interface ChatExportRequester {
  /** The local user's userId at the time of the export request. */
  readonly userId: string;
}

/**
 * Discriminated result. Reasons are deliberately specific so the UI
 * renders distinct copy and the policy self-test can pin every branch.
 */
export type ChatExportPolicyDecision =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason:
        | "tooManyMessages"
        | "requesterNotMember"
        | "channelExportDisabled";
    };

/**
 * Decide whether `requester` may export `channel`.
 *
 * Precedence (top-to-bottom — first failing condition wins):
 *   1. channel-export-disabled — admin flag, takes precedence over
 *      membership so a member of a disabled channel still gets the
 *      "disabled" reason rather than the membership check passing
 *      silently first.
 *   2. requester-not-member — security boundary.
 *   3. too-many-messages — operational cap.
 *   4. (otherwise) → { ok: true }.
 */
export function canExport(
  channel: ChatExportChannel,
  requester: ChatExportRequester,
): ChatExportPolicyDecision {
  if (channel.isExportDisabled) {
    return { ok: false, reason: "channelExportDisabled" };
  }
  if (!channel.memberUserIds.includes(requester.userId)) {
    return { ok: false, reason: "requesterNotMember" };
  }
  if (channel.messageCount > MAX_MESSAGES_PER_EXPORT) {
    return { ok: false, reason: "tooManyMessages" };
  }
  return { ok: true };
}
